import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import type { AdminProfile, AuthContextValue } from "../types/domain";
import {
  getAdminProfile,
  signOutAdmin,
  subscribeToAuthChanges,
} from "../services/auth";
import { toErrorMessage } from "../utils/errors";

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AuthContextValue["profile"]>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Utente per cui il profilo è già stato caricato (undefined = nessuno ancora).
  const hydratedUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    let isActive = true;

    async function hydrate(nextSession: Session | null) {
      if (!isActive) {
        return;
      }

      setSession(nextSession);

      // Refresh del token o ritorno sulla scheda: stesso utente, nessun nuovo caricamento.
      // Evita che la dashboard venga smontata e le visite ricaricate da capo.
      const nextUserId = nextSession?.user.id ?? null;
      if (hydratedUserIdRef.current === nextUserId) {
        return;
      }
      hydratedUserIdRef.current = nextUserId;

      if (!nextUserId) {
        setProfile(null);
        setErrorMessage(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      let nextProfile: AdminProfile | null = null;
      let nextErrorMessage: string | null = null;

      try {
        nextProfile = await getAdminProfile(nextUserId);
      } catch (error) {
        nextErrorMessage = toErrorMessage(error, "Impossibile verificare il profilo admin.");
      }

      if (!isActive) {
        if (hydratedUserIdRef.current === nextUserId) {
          hydratedUserIdRef.current = undefined;
        }
        return;
      }

      if (hydratedUserIdRef.current !== nextUserId) {
        return;
      }

      if (nextErrorMessage) {
        // Permette un nuovo tentativo al prossimo evento di autenticazione.
        hydratedUserIdRef.current = undefined;
      }

      setProfile(nextProfile);
      setErrorMessage(nextErrorMessage);
      setIsLoading(false);
    }

    // INITIAL_SESSION viene emesso alla sottoscrizione. Le chiamate Supabase vengono
    // rimandate fuori dalla callback, come raccomandato da supabase-js per evitare deadlock.
    const {
      data: { subscription },
    } = subscribeToAuthChanges((nextSession) => {
      window.setTimeout(() => {
        hydrate(nextSession).catch((error) => {
          if (isActive) {
            setErrorMessage(
              toErrorMessage(error, "Impossibile aggiornare lo stato di autenticazione."),
            );
            setIsLoading(false);
          }
        });
      }, 0);
    });

    return () => {
      isActive = false;
      subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      profile,
      isAdmin: Boolean(profile?.isAdmin),
      isLoading,
      errorMessage,
      signOut: signOutAdmin,
    }),
    [errorMessage, isLoading, profile, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth deve essere usato dentro AuthProvider.");
  }

  return context;
}
